// internal-deps
import { getCloudinaryUrl, isWebPlatform } from "@swiggy-private/core-ui";
import { IconName } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb/types";
import { IColors, ITypography } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { GlobalHeaderState, SwgyHeaderKeysList } from "@swiggy-private/checkout-core";

// constants
import {
    STATUS_BANNER,
    BOOKING_STATUS,
    COLOR_KEYS,
    PAYMENT_TYPE,
    LIST_ITEM_PADDING,
    TICKET_TEXT,
    TABLE_TEXT,
    EVENT_BOOKING_SHARE_TEXT,
    RESTAURANT_DEEPLINK,
    DEEPLINK_BASE_URL,
    DealCategory,
    TABLE_BOOKING_SHARE_TEXT,
    WITH_OFFER,
    EDP_DEEPLINK,
} from "@dineout-payment-success-ui/constants";

// interfaces
import {
    IAggregatedItemsInfo,
    IBill,
    IBookingDetails,
    ICtx,
    IItem,
    IListItemProp,
    IMetaData,
    SaleMode,
    ITicketAttributes,
} from "@dineout-payment-success-ui/interfaces";
import { format, isEmpty, isEqual, safeJsonParse } from "@swiggy-private/js-utils";
import { ApiResponse } from "@swiggy-private/core-utils";
import { OrderApiData } from "@swiggy-private/payment-types";

export const getListItemProps = (title: string, onClick?: VoidFunction): IListItemProp => ({
    title,
    expandable: true,
    primaryIcon: {
        iconName: "chevronDown16" as IconName,
    },
    expandedIcon: {
        iconName: "chevronUp16" as IconName,
    },
    titleVariant: "V2.body1.neutral.bold" as keyof ITypography,
    customPadding: LIST_ITEM_PADDING,
    onClick,
});

export const getColorCode = (bookingStatus: string) => {
    switch (bookingStatus) {
        case BOOKING_STATUS.CONFIRMED:
            return COLOR_KEYS.POSITIVE as keyof IColors;
        case BOOKING_STATUS.PENDING:
            return COLOR_KEYS.HIGHLIGHT as keyof IColors;
        case BOOKING_STATUS.RESERVED:
            return COLOR_KEYS.HIGHEST_EMPHASIS as keyof IColors;
        case BOOKING_STATUS.CANCELLED:
            return COLOR_KEYS.NEGATIVE as keyof IColors;
        default:
            return COLOR_KEYS.POSITIVE as keyof IColors;
    }
};

export const getStatusBannerMaxHeight = (
    showStatusDescription: boolean,
    hasSubscription: boolean,
    isEventBooking: boolean,
    isPartialPayment?: boolean,
) => {
    if (isEventBooking && !isPartialPayment) {
        return STATUS_BANNER.HEIGHT.EVENT_BOOKING;
    }
    if (showStatusDescription && !isPartialPayment) {
        return hasSubscription ? STATUS_BANNER.HEIGHT.WITH_SUBSCRIPTION : STATUS_BANNER.HEIGHT.WITH_DESCRIPTION;
    }
    return STATUS_BANNER.HEIGHT.DEFAULT;
};

export const getBookingDetails = (item?: IItem): IBookingDetails => {
    return {
        day: item?.bookingDay || "",
        date: item?.bookingDate || "",
        slotGroupTitle: item?.bookingSlot || "",
        time: item?.bookingSlotTime || "",
        noOfGuests: item?.guestCount || 0,
        status: item?.bookingStatus || "",
    };
};
export const getTicketAttributes = (bill: IBill, ctx: ICtx, item: IItem): ITicketAttributes => {
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform);
    const ticketAttributes = bill?.billedItems?.[0]?.item?.ticketAttributes;
    const tnc = ticketAttributes?.ticketTermsAndCondition?.tnc || "";
    const noteText = ticketAttributes?.ticketThingsToRemember?.noteText || "";
    const thingsToRemember = ticketAttributes?.ticketThingsToRemember?.listOfThingsToRemember || [];
    const bookingStatus = item?.bookingStatus;
    return {
        offerDetails: {
            title: ticketAttributes?.ticketTitle || "",
            chipSrc: getCloudinaryUrl(ctx?.ticketPresentation?.ticketLogo || ""),
            containerColorCode: ctx?.ticketPresentation?.colorCodes?.[0] || "",
        },
        showDownloadCta: bookingStatus !== BOOKING_STATUS.CANCELLED && isWebPlatform(platform),
        tnc,
        noteText,
        thingsToRemember,
    };
};
export const checkIsTableReservation = (dealCategory?: string, type?: string): boolean => {
    return (
        !(dealCategory === "" || type === PAYMENT_TYPE.GIRF_PAYMENT) && dealCategory !== DealCategory.PREBOOK_PREPAID
    );
};

export const isStealStoreFlow = (dealCategory?: string): boolean => {
    return dealCategory === DealCategory.PREBOOK_PREPAID;
};

export const isDinecashStealStoreFlow = (saleMode?: SaleMode, dealCategory?: string): boolean => {
    return isEqual(saleMode, SaleMode.DINECASH) && isEqual(dealCategory, DealCategory.PREBOOK_PREPAID);
};

export const isDinecashCouponFlow = (saleMode?: SaleMode, dealCategory?: string): boolean => {
    return isEqual(saleMode, SaleMode.DINECASH) && isEqual(dealCategory, DealCategory.DINECASH_COUPON);
};

export const getBookingStatusHelperText = (isBookingEvent: boolean, bookingStatus: string): string => {
    const varient = bookingStatus === BOOKING_STATUS.PENDING ? "CAPITAL" : "NORMAL";
    if (isBookingEvent) {
        return TICKET_TEXT[varient];
    }
    return TABLE_TEXT[varient];
};

export const getTicketDetailsData = (
    itemsData: IItem[] | undefined,
):
    | {
          itemVariantId: string;
          itemName: string;
          itemId: string;
          outOfStock?: boolean;
          itemPrice?: number;
          quantity?: number;
          tncList?: string[];
          min: number;
          max: number;
      }[]
    | undefined => {
    if (isEmpty(itemsData)) {
        return;
    }

    return itemsData.map((item) => {
        return {
            itemName: item?.savingsLine || "",
            itemPrice: item?.ticketAttributes?.totalEffectivePrice,
            quantity: item?.quantity || 0,
            itemId: item?.itemId,
            itemVariantId: String(item?.itemVariantId),
            tncList: item?.ticketAttributes?.variant?.tncList,
            min: 0,
            max: 10,
        };
    });
};

export const getTotalBill = (metaDataJson: IMetaData | undefined) => metaDataJson?.bill?.totalBill;

export const isShowBillWidget = (totalAmount: number, dealCategory: string) => {
    const showBillWidget = dealCategory !== DealCategory.RESERVATION && totalAmount > 0;

    return showBillWidget;
};

export const getAggregatedItems = (aggregatedItemsInfo?: IAggregatedItemsInfo) => {
    const ticketCountTitle = aggregatedItemsInfo?.ticketCountTitle || "";
    const ticketDateTitle = aggregatedItemsInfo?.dateDisplayTitle || "";
    const ticketTimeTitle = aggregatedItemsInfo?.timeDisplayTitle || "";
    const ticketTitle = aggregatedItemsInfo?.ticketTitle || "";
    return {
        ticketCountTitle,
        ticketDateTitle,
        ticketTimeTitle,
        ticketTitle,
    };
};

export const getEventBookingShareText = (
    eventId: string,
    restaurantInfo?: {
        id: string;
        name: string;
        address: string;
        fullAddress: string;
        city: string;
        locality: string;
    },
    eventName?: string,
    eventDate?: string,
) => {
    const restaurantName = restaurantInfo?.name;
    const city = restaurantInfo?.city || "";
    let restaurantLocality = restaurantInfo?.locality || "";

    const date = eventDate || "";
    const title = eventName || "";

    if (city && city.trim().length !== 0) {
        restaurantLocality = `${restaurantLocality}, ${city}`;
    }
    const deeplink = createEdpDeeplink(eventId);
    const message = format(EVENT_BOOKING_SHARE_TEXT, date, title, restaurantName, restaurantLocality, deeplink);
    return {
        message,
        deeplink,
    };
};

export const getTableBookingShareText = (
    restaurantInfo?: {
        id: string;
        name: string;
        address: string;
        fullAddress: string;
        city: string;
        locality: string;
    },
    bookingInfo?: {
        date?: string;
        timeString?: string;
        guestCount?: number;
        offerText?: string;
    },
) => {
    const restaurantName = restaurantInfo?.name;
    const restaurantId = restaurantInfo?.id || "";
    const city = restaurantInfo?.city || "";
    let restaurantLocality = restaurantInfo?.locality || "";
    const { date, timeString, guestCount, offerText } = bookingInfo || {};

    if (city && city.trim().length !== 0) {
        restaurantLocality = `${restaurantLocality}, ${city}`;
    }
    const deeplink = createRdpDeeplink(restaurantId);
    const message = format(
        TABLE_BOOKING_SHARE_TEXT,
        date,
        timeString,
        guestCount,
        restaurantName,
        restaurantLocality,
        offerText ? format(WITH_OFFER, offerText) : "",
        deeplink,
    );

    return {
        message,
        deeplink,
    };
};

export const createRdpDeeplink = (restId: string) => {
    const deeplinkBaseString = `${format(RESTAURANT_DEEPLINK, restId)}?source=sharing`;
    return DEEPLINK_BASE_URL + encodeURIComponent(deeplinkBaseString);
};

export const createEdpDeeplink = (eventId: string) => {
    return format(EDP_DEEPLINK, eventId);
};

export const getMetaDataFromOrderResponse = (orderRawData?: ApiResponse<OrderApiData> | null) => {
    const orderData = orderRawData?.data?.orders?.[0];
    const confirmOrderData = orderData?.orderJobs?.[0];
    const metadataString = confirmOrderData?.metadata || "{}";
    const metaDataJson = safeJsonParse<IMetaData>(metadataString || "");
    return metaDataJson;
};
